import { NgIf } from '@angular/common';
import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import {
  MatDatepickerModule,
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { endOfDay } from 'date-fns';

interface DateRange {
  dateFrom: Date | null;
  dateTo: Date | null;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styles: `
    :host {
      ::ng-deep .mat-mdc-form-field-icon-suffix {
        display: flex;
        align-items: center;
      }

      ::ng-deep .mat-date-range-input-separator {
        margin: 0 2px !important;
      }

      ::ng-deep .mat-mdc-form-field-subscript-wrapper {
        display: none !important;
      }
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true,
    },
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    TranslateModule,
    MatDateRangePicker,
    MatDateRangeInput,
    MatDatepickerToggle,
    NgIf,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIcon,
  ],
})
export class DateRangePickerComponent implements ControlValueAccessor, OnInit {
  public form: FormGroup;
  public destroyRef = inject(DestroyRef);

  @Input() public label = '';
  @Input() public defaultDates: [Date | string, Date | string] = ['', ''];
  @Input() public minDate?: Date | string;
  @Input() public maxDate?: Date | string;
  @Input() public showClearButton = true;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      dateRange: this.fb.group({
        dateFrom: [],
        dateTo: [],
      }),
    });
  }

  public ngOnInit(): void {
    const [dateFrom, dateTo] = this.defaultDates;
    if (dateFrom && dateTo) {
      this.form.patchValue({
        dateRange: {
          dateFrom,
          dateTo,
        },
      });
    }
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: { dateRange: DateRange }) => {
      const { dateFrom, dateTo } = value.dateRange;

      if (dateFrom || dateTo) {
        this.onChange({ dateFrom, dateTo: endOfDay(dateTo || new Date()) });
      } else if (!dateFrom && !dateTo) {
        // if user clears date, don't send anything
        this.onChange({ dateFrom: null, dateTo: null });
      }
    });
  }

  public clear(event: MouseEvent): void {
    event.stopPropagation();
    this.form.reset();
  }

  public onChange = (value: DateRange): void => {
    //placeholder implementation
    console.log(`Value changed ${value}`);
  };

  public onTouched = (): void => {
    //placeholder implementation
    console.log(`Form control ${DateRangePickerComponent.name} touched`);
  };

  public writeValue(dateRange: DateRange): void {
    this.form.setValue({ dateRange });
  }

  public registerOnChange(onChange: (value: DateRange) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public updateSelected(dateRange: DateRange): void {
    this.onChange(dateRange);
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }
}
